/**
 * Utility functions for generating and attaching E2E (end-to-end) test anchors.
 *
 * @module utils/e2e/getE2EAnchor
 */

import slugify from "@utilities/slugify";

// attribute name used for E2E testing
const ATTRIBUTE_NAME = "data-testid";

/**
 * Generates a unique anchor string for E2E testing based on page, component, and additional identifiers.
 *
 * @param {string} page - The page or context name.
 * @param {string} component - The component name.
 * @param {...(string|number)} rest - Additional identifiers to append to the anchor.
 * @returns {string} The generated anchor string.
 */
export const getE2EAnchor = (
  page: string,
  component: string,
  ...rest: (string | number)[]
) =>
  `${slugify(page)}_${slugify(`${component}${rest?.length ? rest.join("_") : ""}`)}`;

/**
 * Returns an object with the anchor attribute for use in test selectors.
 * <button {...getE2EAnchorAttribute("page", "component")}>
 *
 * @param {string} page - The page or context name.
 * @param {string} component - The component name.
 * @param {...(string|number)} rest - Additional identifiers to append to the anchor.
 * @returns {{ [dataTestId: string]: string }} An object with the anchor attribute.
 */
export const getE2EAnchorAttribute = (
  page: string,
  component: string,
  ...rest: (string | number)[]
) => ({ [ATTRIBUTE_NAME]: getE2EAnchor(page, component, ...rest) });
